import Typography from '@material-ui/core/Typography';
import { FC, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ITeamMemberMapped } from '~/utils/clientAllocation';
import layoutStyles from '../../../shared/components/EditAllocationFormLayout/EditAllocationFormLayout.module.scss';
import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import { IEditAllocationFormValues } from '../../../shared/model/types';
import useLocationGroupAllocationsUpdate from '../../hooks/updateAllocations/useLocationGroupAllocationsUpdate';
import AllocationLocationGroupsSelect from '../SelectFields/AllocationLocationGroupsSelect';
import AllocationLocationGroupRowItem from '../ClientsRowItem/AllocationLocationGroupRowItem';
import { useEditClientAllocationContext } from '../../context/EditClientAllocationContext';
import useAllocationConflictMessage from '../../../shared/hooks/useAllocationConflictMessage';

interface IProps {
  teamMemberIdx: number;
  teamMember: ITeamMemberMapped;
}

const AllocationLocationGroupsList: FC<IProps> = ({ teamMemberIdx, teamMember }): ReactElement => {
  const { locationGroups } = useEditClientAllocationContext();
  const { markChanged, markDeleted } = useAllocationChangeTracking<IEditAllocationFormValues>({
    changedField: 'changedLocationGroupAllocationsIds',
    deletedField: 'deletedLocationGroupAllocationsIds',
  });
  const { fields, updateLocationGroupAllocations, removeLocationGroupAt, removeClientFromGroup } =
    useLocationGroupAllocationsUpdate(teamMemberIdx);
  const { watch, setValue } = useFormContext<IEditAllocationFormValues>();

  const locationGroupAllocations = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  );
  const { activeClientIds, selectedLocationGroupClientIds } = useMemo(
    () =>
      locationGroupAllocations.reduce(
        (acc, lgAlloc) => {
          const removed = lgAlloc.removedClientIds ?? [];
          locationGroups
            .find(locationGroup => locationGroup.locationGroup.id === lgAlloc.locationGroupId)
            ?.clients.filter(client => {
              if (removed.includes(client.id)) return false;
              if (lgAlloc.allocation && !(client.id in lgAlloc.clientMemos)) return false;
              return true;
            })
            .forEach(client => {
              acc.activeClientIds.push(client.id);
              acc.selectedLocationGroupClientIds[client.id] = lgAlloc.locationGroupName;
            });
          return acc;
        },
        {
          activeClientIds: [] as number[],
          selectedLocationGroupClientIds: {} as Record<number, string>,
        },
      ),
    [locationGroupAllocations, locationGroups],
  );

  useEffect(() => {
    setValue(
      `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupClientIds` as const,
      selectedLocationGroupClientIds,
    );
  }, [selectedLocationGroupClientIds, setValue, teamMemberIdx]);

  const { locationGroupListConflict: conflictMessage } = useAllocationConflictMessage({
    teamMemberIdx,
    locationGroups,
  });

  const handleDeleteGroup = useCallback(
    (groupId: number) => {
      const fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      const field = locationGroupAllocations[fieldIdx];
      removeLocationGroupAt(fieldIdx);

      if (field.allocation?.id) {
        const isLastClientInLocationGroup = locationGroupAllocations.length < 2;

        if (isLastClientInLocationGroup) {
          markDeleted(field.allocation.id);
          return;
        }
        markChanged(field.allocation.id);
      }
    },
    [locationGroupAllocations, markChanged, markDeleted, removeLocationGroupAt],
  );

  const handleDeleteClient = useCallback(
    (groupId: number, clientId: number) => {
      const fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      const field = locationGroupAllocations[fieldIdx];
      const currentRemoved = field.removedClientIds ?? [];
      const groupTotalClients =
        locationGroups.find(lg => lg.locationGroup.id === groupId)?.clients.length ?? 0;

      if (groupTotalClients - (currentRemoved.length + 1) <= 0) {
        handleDeleteGroup(groupId);
      } else {
        removeClientFromGroup(fieldIdx, clientId);
        if (field.allocation?.id) markChanged(field.allocation.id);
      }
    },
    [
      handleDeleteGroup,
      locationGroupAllocations,
      locationGroups,
      markChanged,
      removeClientFromGroup,
    ],
  );

  return (
    <>
      {conflictMessage && (
        <Typography variant="body2" className={layoutStyles.conflictAlert}>
          {conflictMessage}
        </Typography>
      )}
      <Typography variant="subtitle1" className={layoutStyles.assignedClientsSectionTitle}>
        Location Groups
      </Typography>

      <AllocationLocationGroupsSelect
        teamMemberIdx={teamMemberIdx}
        updateLocationGroupAllocations={updateLocationGroupAllocations}
        activeClientIds={activeClientIds}
        onDeleteClient={handleDeleteClient}
        onDeleteGroup={handleDeleteGroup}
      />

      {fields.map((field, fieldIdx) => (
        <AllocationLocationGroupRowItem
          key={field.id}
          field={field}
          fieldIdx={fieldIdx}
          teamMemberIdx={teamMemberIdx}
          memberId={teamMember.id}
          onRemoveClient={handleDeleteClient}
          onMarkChanged={markChanged}
        />
      ))}
    </>
  );
};

export default AllocationLocationGroupsList;
